import { z } from 'zod';
import { ACTOR_TYPES, STAFF_ROLES, THEMES } from '../enums.js';
import { CAPABILITIES } from '../roles.js';
import { uuidSchema } from './common.js';
/**
 * Shape of `GET /api/me`.
 *
 * This is a testing and orientation surface, not a product screen. Every number
 * in `scope` comes from a query the caller ran through their OWN RLS-scoped
 * connection, so the response is a direct readout of what the database is willing
 * to show that actor — which makes it the fastest honest way to confirm the
 * boundary works from the browser rather than from psql.
 */
export const sessionActorSchema = z.object({
    userId: uuidSchema,
    email: z.string().email(),
    name: z.string(),
    /**
     * Server-authoritative for non-admin staff, via the users_self_update
     * policy. The admin row can't reach that policy at all (see its comment
     * in rls-policies.sql), so this value is a display default only for the
     * admin — the frontend keeps that one account's toggle in localStorage.
     */
    theme: z.enum(THEMES),
    /**
     * Saved staff dashboard widget order, an opaque list of widget ids. Same
     * admin caveat as `theme`: server-authoritative for non-admin staff, but
     * the admin row can't reach `users_self_update`, so the frontend keeps
     * that one account's order in localStorage instead. `null` means "no
     * saved order yet" — render the default order.
     */
    dashboardLayout: z.array(z.string()).nullable(),
    actorType: z.enum(ACTOR_TYPES),
    staffRole: z.enum(STAFF_ROLES).nullable(),
    lastLoginAt: z.string().datetime().nullable(),
    /**
     * The actor's OWN scope key, so the portal can address its own record —
     * `GET /api/owners/:id` and `GET /api/tenants/:id` need an id, and making the
     * client guess it by listing and taking the only row RLS returned would work
     * right up until a second row appeared.
     *
     * Exactly one is set for a portal actor and neither for staff, matching
     * `actorSchema`. Returning it discloses nothing: it is the caller's own id, and
     * every other id remains unreachable because the policies say so, not because
     * the client does not know them.
     */
    ownerId: uuidSchema.nullable(),
    tenantId: uuidSchema.nullable(),
    applicantId: uuidSchema.nullable(),
    vendorId: uuidSchema.nullable(),
    /**
     * What this role may do, computed server-side by `capabilitiesFor`.
     *
     * Sent rather than derived in the browser so the console and the API guards read
     * from the same evaluation. Deriving it client-side would work — the table is in
     * `packages/shared` either way — but it would let a stale bundle disagree with
     * the running API about who may press a button, and the resulting 403 looks like
     * a bug rather than a rule.
     *
     * This is display logic. It is not what stops anyone: see the header of
     * `roles.ts`.
     */
    capabilities: z.array(z.enum(CAPABILITIES)),
});
/** Row counts the actor can actually reach. Zeros are meaningful, not errors. */
export const sessionScopeSchema = z.object({
    properties: z.number().int(),
    units: z.number().int(),
    leases: z.number().int(),
    tenants: z.number().int(),
    openCharges: z.number().int(),
    /**
     * DEPOSIT_NOTICE / DAMAGES_NOTICE rows past `dueBy` with no `sentAt`. This is
     * the caller's own RLS-scoped count — for staff that's the whole portfolio,
     * for a tenant it can only ever be a notice on their own lease.
     */
    overdueNotices: z.number().int(),
    /**
     * work_orders not yet CANCELED/COMPLETED. Zero for every portal actor —
     * there's no owner or tenant policy on the table at all.
     */
    openWorkOrders: z.number().int(),
    /**
     * Rows visible in `tenant_sensitive`. Expected to be 0 for every tenant and
     * owner, and for MAINTENANCE staff (invariant 5). Surfaced rather than hidden
     * so a regression is visible on the page instead of only in the RLS suite.
     */
    tenantSensitive: z.number().int(),
    /**
     * Rows visible in `applications` — for an applicant, their own; for staff,
     * the whole portfolio (`applications_staff_all`); zero for owners and
     * tenants, since neither has any policy on the table.
     */
    applications: z.number().int(),
});
/** Money as a decimal string — never a float (invariant 2). */
export const sessionBalanceSchema = z.object({
    leaseId: uuidSchema,
    propertyName: z.string(),
    unitLabel: z.string(),
    totalBalance: z.string(),
    pastDueBalance: z.string(),
    maxDaysLate: z.number().int(),
    /**
     * Sum of this lease's PENDING/PROCESSING payments — submitted but not yet
     * settled, so not yet reflected in `totalBalance`/`pastDueBalance`. Optional
     * because the staff rent-roll/delinquency reports (`RentRollRow`, a
     * structurally similar but separate shape) share `BalanceTable` with this
     * endpoint and don't compute it.
     */
    pendingPaymentAmount: z.string().optional(),
});
export const meResponseSchema = z.object({
    actor: sessionActorSchema,
    scope: sessionScopeSchema,
    balances: z.array(sessionBalanceSchema),
});
//# sourceMappingURL=session.js.map